import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogOut, ArrowLeft, Plus, Pencil, Trash2, Loader2, ShieldAlert, Mail, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_LABELS = {
  1: "Nível 1 — Operacional",
  2: "Nível 2 — Coordenador",
  3: "Nível 3 — Gestor",
  4: "Nível 4 — Diretor",
  5: "Nível 5 — Administração",
};

const LEVEL_COLORS = {
  1: "bg-slate-100 text-slate-700 border-slate-300",
  2: "bg-blue-50 text-blue-700 border-blue-300",
  3: "bg-amber-50 text-amber-700 border-amber-300",
  4: "bg-orange-50 text-orange-700 border-orange-300",
  5: "bg-red-50 text-red-700 border-red-300",
};

const AdminApprovers = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", level: 1, margin_min: 0, margin_max: 15, emails: [], active: true });
  const [emailInput, setEmailInput] = useState("");

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/approval-categories`, { headers: getAuthHeaders() });
      setCategories(r.data);
    } catch (e) {
      toast.error("Erro a carregar categorias");
    } finally { setLoading(false); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", description: "", level: 1, margin_min: 0, margin_max: 15, emails: [], active: true });
    setEmailInput("");
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    // Migração: se categoria legada só tem threshold, mostrar como [0, threshold]
    const hasInterval = c.margin_min !== undefined && c.margin_min !== null;
    setForm({
      name: c.name,
      description: c.description || "",
      level: c.level || 1,
      margin_min: hasInterval ? c.margin_min : 0,
      margin_max: hasInterval ? (c.margin_max ?? "") : (c.margin_threshold ?? 15),
      emails: c.emails || [],
      active: c.active,
    });
    setEmailInput("");
    setDialogOpen(true);
  };

  const addEmail = () => {
    const e = emailInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Email inválido"); return; }
    if (form.emails.includes(e)) { toast.error("Email já adicionado"); return; }
    setForm({ ...form, emails: [...form.emails, e] });
    setEmailInput("");
  };

  const removeEmail = (e) => setForm({ ...form, emails: form.emails.filter(x => x !== e) });

  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim()) { toast.error("Nome obrigatório"); return; }
    if (form.emails.length === 0) { toast.error("Adicione pelo menos um email"); return; }
    try {
      const payload = {
        ...form,
        level: Number(form.level),
        margin_min: Number(form.margin_min) || 0,
        margin_max: form.margin_max === "" || form.margin_max === null ? null : Number(form.margin_max),
      };
      if (editing) {
        await axios.put(`${API}/approval-categories/${editing.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Categoria atualizada");
      } else {
        await axios.post(`${API}/approval-categories`, payload, { headers: getAuthHeaders() });
        toast.success("Categoria criada");
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminar categoria "${c.name}"?`)) return;
    await axios.delete(`${API}/approval-categories/${c.id}`, { headers: getAuthHeaders() });
    toast.success("Categoria eliminada");
    load();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-approvers-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-3 py-1.5">
              <img src="/evolutt_logo_white.png" alt="Evolutt" className="h-8" />
            </div>
            <p className="text-xs text-muted-foreground">Permissões de Negócio</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="w-4 h-4 mr-2" />Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin"><Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button></Link>
          <div className="flex-1">
            <h1 className="font-manrope font-extrabold text-2xl flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-[var(--brand-primary)]" />
              Permissões de Negócio
            </h1>
            <p className="text-muted-foreground text-sm">
              Categorias de aprovadores por grau de responsabilidade — múltiplos emails associados para fluxo de aprovação de propostas/margens.
              <br />
              <span className="text-amber-700 text-xs">⚠ O envio de email automático está em standby (SendGrid). Por agora as categorias são apenas configuradas.</span>
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-600 text-white hover:bg-brand-700 font-bold" onClick={openNew} data-testid="new-approver-btn">
                <Plus className="w-4 h-4 mr-2" />Nova Categoria
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Editar" : "Nova"} Categoria de Aprovador</DialogTitle>
                <DialogDescription>
                  Defina o nome da categoria, o grau de responsabilidade e os emails que recebem o pedido de aprovação.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome da categoria</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="ex.: Aprovação Comercial"
                      required
                      data-testid="approver-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Grau de responsabilidade</Label>
                    <Select value={String(form.level)} onValueChange={(v) => setForm({ ...form, level: Number(v) })}>
                      <SelectTrigger data-testid="approver-level-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(LEVEL_LABELS).map(([k, label]) => (
                          <SelectItem key={k} value={k}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Intervalo de Margem (%)</Label>
                  <div className="grid grid-cols-2 gap-3 max-w-md">
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">Margem mín. (inclusivo)</span>
                      <div className="relative">
                        <Input
                          type="number" min="0" max="100" step="0.5"
                          value={form.margin_min}
                          onChange={(e) => setForm({ ...form, margin_min: e.target.value })}
                          className="pr-8 font-mono"
                          data-testid="approver-margin-min"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">Margem máx. (exclusivo)</span>
                      <div className="relative">
                        <Input
                          type="number" min="0" max="100" step="0.5"
                          value={form.margin_max}
                          onChange={(e) => setForm({ ...form, margin_max: e.target.value })}
                          className="pr-8 font-mono"
                          placeholder="Sem limite"
                          data-testid="approver-margin-max"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Ex.: <strong>0–10%</strong> aciona esta categoria quando a margem está entre 0% e 10%. Deixe o máximo vazio para "sem limite".
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Em que situações esta categoria deve ser acionada"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Emails dos aprovadores</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                      placeholder="aprovador@empresa.pt"
                      data-testid="approver-email-input"
                    />
                    <Button type="button" variant="outline" onClick={addEmail} data-testid="add-email-btn">
                      <Plus className="w-4 h-4 mr-1" />Adicionar
                    </Button>
                  </div>
                  {form.emails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2 p-3 bg-slate-50 rounded">
                      {form.emails.map(e => (
                        <Badge key={e} variant="secondary" className="gap-1 pr-1">
                          <Mail className="w-3 h-3" />{e}
                          <button
                            type="button"
                            onClick={() => removeEmail(e)}
                            className="ml-1 hover:bg-slate-300 rounded p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between border-t pt-4">
                  <Label>Ativo</Label>
                  <Switch checked={form.active} onCheckedChange={(c) => setForm({ ...form, active: c })} />
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-brand-600 text-white hover:bg-brand-700" data-testid="submit-approver-btn">
                    {editing ? "Guardar" : "Criar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Nível</TableHead>
                    <TableHead>Intervalo Margem</TableHead>
                    <TableHead>Aprovadores</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                        Sem categorias. Crie a primeira para começar a configurar o fluxo de aprovação.
                      </TableCell>
                    </TableRow>
                  ) : categories.map(c => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <div className="font-medium">{c.name}</div>
                        {c.description && <p className="text-xs text-muted-foreground mt-0.5">{c.description}</p>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={LEVEL_COLORS[c.level] || ""}>
                          {LEVEL_LABELS[c.level] || `Nível ${c.level}`}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {(() => {
                            const hasInterval = c.margin_min !== undefined && c.margin_min !== null;
                            if (!hasInterval) return `< ${c.margin_threshold ?? 15}%`;
                            const max = c.margin_max === null || c.margin_max === undefined ? "∞" : `${c.margin_max}%`;
                            return `${c.margin_min}% – ${max}`;
                          })()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-md">
                          {(c.emails || []).map(e => (
                            <Badge key={e} variant="secondary" className="gap-1 text-xs">
                              <Mail className="w-3 h-3" />{e}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.active
                          ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Ativo</Badge>
                          : <Badge variant="secondary">Inativo</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(c)}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AdminApprovers;
